import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthCallback from "@/pages/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import WebhookGuide from "@/pages/WebhookGuide";
import BuyCredits from "@/pages/BuyCredits";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import AdminModeration from "@/pages/AdminModeration";
import Landing from "@/pages/Landing";
import Inquiries from "@/pages/Inquiries";
import RespondInquiry from "@/pages/RespondInquiry";
import { Radio } from "lucide-react";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex items-center gap-2 text-muted-foreground animate-pulse">
      <Radio className="w-5 h-5 text-[#007AFF]" />
      <span className="font-mono text-sm">Loading…</span>
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  // Credit lock: when a user's balance runs out, the whole app is gated behind buying credits.
  if (user.locked && location.pathname !== "/credits") {
    return <Navigate to="/credits" replace />;
  }
  return children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

const HomeGate = () => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (user && user.locked) return <Navigate to="/credits" replace />;
  return user ? <Dashboard /> : <Landing />;
};

function AppRoutes() {
  const location = useLocation();
  // Google OAuth return: session_id arrives in the URL fragment; process it before any route guards.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route path="/respond/:token" element={<RespondInquiry />} />
      <Route path="/" element={<HomeGate />} />
      <Route path="/inquiries" element={<Protected><Inquiries /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/webhook" element={<Protected><AdminOnly><WebhookGuide /></AdminOnly></Protected>} />
      <Route path="/credits" element={<Protected><BuyCredits /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/moderation" element={<Protected><AdminModeration /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
